// mindmetal-app/web/src/components/mindmetal/SignalDiffViewer.tsx
import { motion } from 'framer-motion'
import { ArrowRight, Plus, RefreshCw } from 'lucide-react'
import { Badge, cn } from '@personic/shared'
import type { SignalDiff } from '@personic/shared'

interface SignalDiffViewerProps {
  diffs: SignalDiff[]
  className?: string
}

export function SignalDiffViewer({ diffs, className }: SignalDiffViewerProps) {
  const newSignals = diffs.filter((d) => d.change_type === 'new')
  const updatedSignals = diffs.filter((d) => d.change_type === 'updated')

  if (newSignals.length === 0 && updatedSignals.length === 0) {
    return (
      <div className={cn('text-center py-4 text-warm-500 text-sm', className)}>
        No signal changes detected
      </div>
    )
  }

  return (
    <div className={className}>
      {newSignals.length > 0 && (
        <div className="mb-4">
          <h5 className="flex items-center gap-2 text-sm font-medium text-sage-700 mb-2">
            <Plus className="w-4 h-4" />
            New Signals ({newSignals.length})
          </h5>
          <div className="space-y-2">
            {newSignals.map((diff, i) => (
              <motion.div
                key={diff.dimension}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center justify-between bg-sage-50 rounded-lg px-3 py-2"
              >
                <span className="text-sm text-midnight-700 font-mono">{diff.dimension}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="sage">{(diff.new_value * 100).toFixed(0)}%</Badge>
                  <span className="text-xs text-warm-500">
                    conf: {(diff.new_confidence * 100).toFixed(0)}%
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {updatedSignals.length > 0 && (
        <div>
          <h5 className="flex items-center gap-2 text-sm font-medium text-copper-700 mb-2">
            <RefreshCw className="w-4 h-4" />
            Updated Signals ({updatedSignals.length})
          </h5>
          <div className="space-y-2">
            {updatedSignals.map((diff, i) => (
              <motion.div
                key={diff.dimension}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center justify-between bg-copper-50 rounded-lg px-3 py-2"
              >
                <span className="text-sm text-midnight-700 font-mono">{diff.dimension}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-warm-500">
                    {diff.old_value !== null ? `${(diff.old_value * 100).toFixed(0)}%` : '—'}
                  </span>
                  <ArrowRight className="w-3 h-3 text-warm-400" />
                  <Badge variant="copper">{(diff.new_value * 100).toFixed(0)}%</Badge>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
